import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import http from 'apis/config/vendor-http-service';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'warehouse-items';
export const getKasoItemsEndpointIdentifier = endpoint;

type TData = Record<string, any>;
type TError = AxiosError;
type TQueryParams = {
  page: TParam;
  limit: TParam;
  name: TParam;
  filter: TParam;
  subEntityId?: TParam;
  subEntityType?: TParam;
  mapped: TParam;
};
type TQueryKey = [typeof endpoint, TQueryParams];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetKasoWarehouseItemsApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { name, filter, subEntityId, subEntityType, mapped } = getPageParams();

  const queryFn = async () => {
    try {
      const { data } = await http.get(endpoint, {
        baseURL: `${process.env.REACT_APP_BASE_URL}/pos/v2`,
        params: { page, limit, name, filter, subEntityId, subEntityType, mapped },
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: 'Something went wrong! Try again later',
        status: 'error',
      });

      throw error;
    }
  };

  const queryParams: TQueryParams = { page, limit, name, filter, mapped };

  if (subEntityType && subEntityId) {
    queryParams.subEntityId = subEntityId;
    queryParams.subEntityType = subEntityType;
  }

  return useQueryWrapper([endpoint, queryParams], queryFn, {
    enabled: isLoggedIn,
    ...options,
  });
}
